import React, { useEffect, useRef, useState, useMemo } from "react";
import { Button, Col, Row, Tab, Nav } from "react-bootstrap";
import { FileEarmarkText, Eye, Gear, Pen, Plus } from "react-bootstrap-icons";
import { MdDelete } from "react-icons/md";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import ConfirmationModal from "components/common/custom/confirmation-modal";
import ServiceInformationDetails from "./ServiceInformationDetails";
import moment from "moment";
import {
  handleAddClaimServices,
  handleFillStepData,
  resetSelectedService,
  setEditModal,
  setHistoryModal,
  setSelectedService,
  setViewModal,
} from "features/cliams/config/claimSlice";
import {
  GetAllPractitonerList,
  GetAllServiceCodeDescription,
  GetSearchLabCode,
} from "features/preauthorization/config/preauthorizationApi";
import { GetAllRoute } from "../../preauthorization/config/preauthorizationApi";
import { timeInstruction } from "../../preauthorization/components/ServicesProcedures";

const ServiceViewRequest = () => {
  const dispatch = useDispatch();
  const {
    cliamServicesList,
    claimRequestData,
    viewClaimInfo,

    stepFormData,
    preauthorizationDiagnosis,
    practitionerListAdded,
  } = useSelector((state) => state.claim);
  const { allPermissionType } = useSelector((state) => state.role);
  const claimsSetting = allPermissionType?.[0]?.rolemngmt_name?.find(
    (item) =>
      item?.rolemngmttyp_name === "Claim" ||
      item?.rolemngmttyp_name === "Claims",
  );

  const {
    bodySite,
    routeList,
    scientificAbsnceReason,
    bodySubSite,
    preauthorizationType,
    oralDentalList,
    substituteReason,
    practitionerList,
    durationList,
    serviceTypeList,
    preauthorizationRequest,
    selectReasonList,
  } = useSelector((state) => state.preauthorization);

  const payloadClaim = viewClaimInfo?.payload_json;
  // Memoize claimReq, supportingInfo, diagnosis, careTeamMembers, and payloadService
  const claimReq = useMemo(
    () =>
      claimRequestData?.entry?.find(
        (e) => e?.resource?.resourceType === "Claim",
      )?.resource ||
      preauthorizationRequest?.entry?.find(
        (e) => e?.resource?.resourceType === "Claim",
      )?.resource ||
      {},
    [claimRequestData, preauthorizationRequest],
  );

  useEffect(() => {
    if (practitionerList?.length === 0) {
      dispatch(GetAllPractitonerList());
    }
  }, []);

  const diagnosisPayload =
    Object.values(viewClaimInfo?.payload_json?.diag ?? {}) || [];

  const services = useMemo(() => claimReq?.item || [], [claimReq]);
  const payloadService = useMemo(
    () => Object.values(viewClaimInfo?.payload_json?.prd_ser || {}),
    [viewClaimInfo?.payload_json?.prd_ser],
  );

  const prescription = useMemo(
    () =>
      stepFormData?.prescription ||
      payloadClaim?.prescript_submt ||
      claimReq?.use === "predetermination",
    [stepFormData?.prescription, claimReq?.use],
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [searchParams] = useSearchParams();
  const edit = searchParams.get("edit") === "true";
  const preauth = searchParams.get("preauth") === "true";
  const selectedBranch = JSON.parse(localStorage.getItem("selectedBranch"));
  const status = viewClaimInfo?.claim_status;
  const deletedServiceIds = useRef(new Set());
  const isDisabled = [
    "invalid",
    "ready to submit",
    "rejected",
    "cancelled",
    "in process",
    "validation error",
    "invalid",
    "no response",
    "nores",
  ].includes(status?.toLowerCase());

  useEffect(() => {
    if (routeList?.length === 0) {
      dispatch(GetAllRoute());
    }
  }, []);

  const hasProcessedServices = useRef(false);

  const daySupply = viewClaimInfo?.provider_request?.sup_info_suply;

  useEffect(() => {
    const hasPractitioners =
      Object.keys(viewClaimInfo?.payload_json?.pract_id || {}).length > 0;
    const hasDiagnoses =
      Object.keys(viewClaimInfo?.payload_json?.diag || {}).length > 0;

    if (
      cliamServicesList?.length > 0 ||
      (practitionerListAdded?.length === 0 && hasPractitioners) ||
      (preauthorizationDiagnosis?.length === 0 && hasDiagnoses) ||
      deleteId ||
      hasProcessedServices.current
    )
      return;

    dispatch(
      handleFillStepData({
        check: "claim_type",
        value:
          preauthorizationType?.find(
            (item) => item?.claim_types_code === viewClaimInfo?.claim_type,
          ) || "",
      }),
    );

    const processServices = async () => {
      try {
        const allServices = [];

        const additionalPayloadServices = await Promise.all(
          payloadService
            .filter((viewService, index) => {
              // Use provider_code and index as the unique discriminator so two identical services are both kept.
              const stableId = `${viewService.prd_code}-${viewService.prov_cde || viewService.period?.start || ""}-${index}`;
              return !deletedServiceIds.current.has(stableId);
            })
            .map(async (viewService, index) => {
              const providerCode = viewService.prd_type || "itm_prdt";
              const practId =
                viewService?.pract?.length > 0
                  ? viewService?.pract?.[0]
                  : viewService?.pract;

              let servicePractitioner = null;
              if (practId) {
                servicePractitioner = practitionerListAdded?.find(
                  (item) =>
                    item?.practitioner?.value?.practitioner_id == practId ||
                    item?.practitioner?.value?.practitioner_license == practId,
                );
              }
              if (!servicePractitioner) {
                servicePractitioner = practitionerListAdded?.find(
                  (item) =>
                    item?.role?.careroles_code?.toLowerCase() === "primary",
                );
              }

              const servicePractFullName =
                servicePractitioner?.pract_name?.split(" ");
              const servDoctor = servicePractitioner
                ? {
                    value: servicePractitioner,
                    label: `(${
                      servicePractitioner?.practitioner?.value
                        ?.practitioner_license || ""
                    }) - ${
                      servicePractitioner?.practitioner?.value
                        ?.practitioner_title || "Dr"
                    } ${
                      servicePractitioner?.practitioner?.value
                        ?.practitioner_firstname ||
                      servicePractFullName?.[0] ||
                      ""
                    } ${
                      servicePractitioner?.practitioner?.value
                        ?.practitioner_familyname ||
                      servicePractFullName?.[1] ||
                      ""
                    }`,
                  }
                : null;

              let serviceCodeResponse = null;
              // Cache the matched service entry once to avoid repeated .find() calls below.
              let matchedService = null;
              try {
                const response = await dispatch(
                  GetAllServiceCodeDescription({
                    branch_id:
                      selectedBranch?.branch_id || selectedBranch?.value || "",
                    prd_typ: providerCode,
                    prodserv_id: viewService.prd_code,
                    ins_sel: stepFormData?.payer?.insurance_company_id || "",
                    claim_type: stepFormData?.claim_type?.claim_types_id || "",
                    is_prescription: prescription,
                  }),
                ).unwrap();
                serviceCodeResponse = response;
                matchedService =
                  response?.data?.Service_Code?.find(
                    (i) => i?.provider_code == viewService?.prov_cde,
                  ) ?? null;
              } catch (error) {
                console.error("Error fetching service code description", error);
              }

              let prescribedMedicationType = {};
              let prescribedMedicationCode = null;
              if (viewService.pres_med?.code) {
                try {
                  const medResponse = await dispatch(
                    GetAllServiceCodeDescription({
                      branch_id:
                        selectedBranch?.branch_id ||
                        selectedBranch?.value ||
                        "",
                      prd_typ: viewService.pres_med.type || "itm_prdt",
                      prodserv_id: viewService.pres_med.code,
                      ins_sel: stepFormData?.payer?.insurance_company_id || "",
                      claim_type:
                        stepFormData?.claim_type?.claim_types_id || "",
                      is_prescription: prescription,
                    }),
                  ).unwrap();

                  prescribedMedicationType = serviceTypeList.find(
                    (type) => type.servtype_code === viewService.pres_med.type,
                  ) || {
                    servtype_code: viewService.pres_med.type || "itm_prdt",
                    servtype_name: viewService.pres_med.type || "Unknown",
                  };

                  // Cache the matched med entry once to avoid repeated .find() calls.
                  const matchedMedService =
                    medResponse?.data?.Service_Code?.find(
                      (i) => i?.provider_code == viewService?.prov_cde,
                    ) ?? null;

                  prescribedMedicationCode = {
                    prodserv_code:
                      matchedMedService?.prodserv_code ||
                      viewService.pres_med.code ||
                      "",
                    prodserv_desc:
                      matchedMedService?.prodserv_desc ||
                      viewService.pres_med.code ||
                      "",
                    provider_code: matchedMedService?.provider_code || "",
                    unit_price: matchedMedService?.unit_price || 0,
                    price_discount: matchedMedService?.price_discount || 0,
                    price_tax: matchedMedService?.price_tax || "15",
                    provider_codedesc:
                      matchedMedService?.provider_codedesc || "",
                    prdurl:
                      matchedMedService?.prdurl ||
                      viewService.pres_med.url ||
                      "",
                    prodserv_unittyp: matchedMedService?.prodserv_unittyp || "",
                    prodserv_pcksze: matchedMedService?.prodserv_pcksze || "",
                    prodserv_strngt: matchedMedService?.prodserv_strngt || "",
                  };
                } catch (error) {
                  console.error(
                    "Error fetching prescribed medication details",
                    error,
                  );
                }
              }

              const labTests = await Promise.all(
                (viewService.supinfo1 || []).map(async (labCode) => {
                  try {
                    const response = await dispatch(
                      GetSearchLabCode({ labcde_id: labCode }),
                    ).unwrap();
                    const labData = response?.data?.lab_codes?.[0];
                    if (!labData) return null;
                    return {
                      loinc_code: labData?.loinc_code || labCode,
                      loinc_description: labData?.loinc_description || "",
                      loinc_uom: labData?.loinc_uom || "",
                    };
                  } catch (err) {
                    return null;
                  }
                }),
              );

              const validLabTests = labTests.filter(Boolean);

              // Use provider_code and index as unique discriminator — must match the filter above.
              const stableId = `${viewService.prd_code}-${viewService.prov_cde || viewService.period?.start || ""}-${index}`;

              return {
                id: stableId,
                service_start_date:
                  viewService.period?.start || viewService?.servicedDate || "",
                service_end_date:
                  viewService.period?.end || viewService?.servicedDate || "",
                service_type: serviceTypeList?.find(
                  (item) => item?.servtype_code === viewService.prd_type,
                ) || {
                  servtype_code: viewService.prd_type,
                  servtype_name: viewService.prd_type || "",
                },
                // matchedService was resolved once above — reused here for all fields.
                service_code_description: {
                  prodserv_code:
                    matchedService?.prodserv_code || viewService.prd_code || "",
                  prodserv_desc:
                    matchedService?.prodserv_desc ||
                    viewService.prd_descp_txt ||
                    "",
                  provider_code:
                    matchedService?.provider_code || viewService.prov_cde || "",
                  unit_price:
                    matchedService?.unit_price || viewService.prd_pri || 0,
                  price_discount:
                    matchedService?.price_discount || viewService.discnt || 0,
                  price_tax:
                    matchedService?.price_tax || viewService.diag_tax || "15",
                  provider_codedesc:
                    matchedService?.provider_codedesc ||
                    viewService.prov_cde ||
                    "",
                  prdurl:
                    matchedService?.prdurl || viewService.prd_url_txt || "",
                  prodserv_unittyp: matchedService?.prodserv_unittyp || "",
                  prodserv_pcksze: matchedService?.prodserv_pcksze || "",
                  prodserv_strngt: matchedService?.prodserv_strngt || "",
                  value: matchedService?.value || "",
                  label: matchedService?.label || "",
                },
                prescribed_medication_type: prescribedMedicationType,
                prescribed_medication_code: prescribedMedicationCode,
                maternity: viewService.prd_mtrnty || false,
                unit_price: viewService.prd_pri || 0,
                invoice_no: viewService.invoice_no || "",
                discount: viewService.discnt || "0",
                quantity: viewService.prd_qty || 1,
                preauthorization_ref_no:
                  viewService?.preauth_ref || viewService?.preauthref,
                vat: viewService.diag_tax > 0 ? 15 : 0,
                net_amount: viewService.prd_namnt || 0,
                day_supply: daySupply?.[index]?.qty,
                patient_share: viewService.prd_pshre || 0,
                diagnosis: viewService.diag
                  ? preauthorizationDiagnosis?.filter((item) =>
                      viewService.diag?.includes(item.code),
                    )
                  : preauthorizationDiagnosis?.filter(
                      (item) => item?.type == "principal",
                    ) || [],
                service_practitioner: servDoctor || null,
                lab_tests: validLabTests,
                scientic_code_absence:
                  scientificAbsnceReason?.find(
                    (item) =>
                      item?.absrsn_code === viewService.Sntfc_code_abs_rsn,
                  ) || null,
                scientific_code_other_reason:
                  viewService.Sntfc_code_abs_rsn_oth || "",
                strength: viewService.strngth || "",
                pharmacist_substitute:
                  substituteReason?.find(
                    (sub) =>
                      sub?.subsrsn_code?.toLowerCase() ===
                      viewService?.pharsubs,
                  ) || null,

                substitute_reason_other: viewService.pharsubsrsn || "",
                selection_reason:
                  selectReasonList?.find(
                    (item) => item?.pharmselrsn_code === viewService.pselrsn,
                  ) || null,
                start_date: viewService.duration?.start
                  ? new Date(viewService.duration.start)
                  : "",
                end_date: viewService.duration?.end
                  ? new Date(viewService.duration.end)
                  : "",
                duration: viewService.duration?.dur_time || "",
                durationType:
                  durationList?.find(
                    (dur) =>
                      dur?.ucumtype_name?.toLowerCase() ===
                      viewService.duration?.dur_time_value?.toLowerCase(),
                  )?.ucumtype_code ||
                  viewService.duration?.dur_time_value ||
                  null,
                frequency: viewService.frequency?.frq || "",
                period_info: viewService.frequency?.frq_prd || "",
                period_type:
                  durationList?.find(
                    (item) =>
                      item?.ucumtype_name?.toLowerCase() ===
                      viewService.frequency?.frq_time_value,
                  )?.ucumtype_code ||
                  viewService.frequency?.frq_time_value ||
                  "",
                body_site: viewService.body_site
                  ? bodySite?.find(
                      (item) => item?.bodysite_code === viewService.body_site,
                    ) || null
                  : null,
                sub_site: viewService.orl_reg_subsite
                  ? bodySubSite?.find(
                      (item) =>
                        item?.bodysubsite_code === viewService.orl_reg_subsite,
                    ) || null
                  : null,
                dose: viewService.dose?.dsg_time_value || "",
                dose_type: viewService.dose?.dsg_inst || "",
                route:
                  routeList?.find(
                    (route) =>
                      route?.ruteadmn_code?.toLowerCase() ===
                      viewService.ROA?.toLowerCase(),
                  ) || null,
                time_instruction:
                  timeInstruction?.find(
                    (item) => item?.time_instCode === viewService.time_inst,
                  ) || null,
                refill_count: viewService.refill_count || "",
                patient_instruction: viewService.pat_inst || "",
                dosage_instruction: viewService.dosage_instruction || "",
                oral_origin:
                  oralDentalList?.find(
                    (item) => item?.oral_code === viewService.orl_reg_code,
                  ) || null,
              };
            }),
        );

        allServices.push(...additionalPayloadServices);

        // Add services to cliamServicesList if they don't already exist,
        // keyed by id (which encodes prd_code + provider_code).
        const addedIds = new Set(cliamServicesList?.map((s) => s.id) || []);
        allServices.forEach((service) => {
          if (!addedIds.has(service.id)) {
            dispatch(
              handleAddClaimServices({
                type: "ADD_SERVICES",
                payload: service,
              }),
            );
            addedIds.add(service.id);
          }
        });

        hasProcessedServices.current = true;
      } catch (err) {
        console.error("Error processing services", err);
      }
    };

    processServices();

    return () => {
      hasProcessedServices.current = false;
    };
  }, [
    dispatch,
    edit,
    preauth,
    services,
    serviceTypeList,
    viewClaimInfo,
    routeList,
    durationList,
    substituteReason,
    selectReasonList,
    payloadService,
    practitionerListAdded,
    preauthorizationDiagnosis,
  ]);

  const groupServicesByInvoice = () => {
    const grouped = {};

    cliamServicesList?.forEach((service) => {
      const invoiceNo = service.invoice_no || "Uninvoiced";

      if (!grouped[invoiceNo]) {
        grouped[invoiceNo] = {
          invoiceNumber: invoiceNo,
          services: [],
          totalAmount: 0,
        };
      }

      grouped[invoiceNo].services.push(service);
      grouped[invoiceNo].totalAmount += Number(service.net_amount || 0);
    });

    return grouped;
  };

  const groupedInvoices = groupServicesByInvoice();
  const invoiceKeys = Object.keys(groupedInvoices);

  const [activeTab, setActiveTab] = useState("all-services");

  console.log({ preauthorizationDiagnosis });

  useEffect(() => {
    if (
      !activeTab ||
      (activeTab !== "all-services" && !invoiceKeys.includes(activeTab))
    ) {
      setActiveTab("all-services");
    }
  }, [invoiceKeys, activeTab]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const viewModal = (id, row) => {
    dispatch(setSelectedService(row));
    dispatch(setHistoryModal(true));
    dispatch(setViewModal(true));
  };

  const editModal = (id, row) => {
    dispatch(setSelectedService(row));
    dispatch(setHistoryModal(true));
    dispatch(setEditModal(true));
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      deletedServiceIds.current.add(deleteId);
      dispatch(
        handleAddClaimServices({
          type: "DELETE_SERVICES",
          payload: { id: deleteId },
        }),
      );
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const columns = [
    {
      name: <div className="fs-12">Service Code / Description</div>,
      sortable: false,
      omit: false,
      cell: (row) => {
        const code =
          row?.service_code_description?.value ||
          row?.service_code_description?.prodserv_code;
        const desc =
          row?.service_code_description?.label ||
          row?.service_code_description?.prodserv_desc;
        const providerCode = row?.service_code_description?.provider_code;
        const providerDesc = row?.service_code_description?.provider_codedesc;

        return (
          <div className="fs-12">
            <div className="text-warning">
              <strong>{code}</strong> - {desc}
            </div>
            {(providerCode || providerDesc) && (
              <div className="fs-12 text-primary ">
                {providerCode || ""} - {providerDesc || ""}
              </div>
            )}
          </div>
        );
      },
      width: "150px",
    },
    {
      name: <div className="fs-12">Date</div>,
      sortable: false,
      omit: false,
      selector: (row) => (
        <div className="fs-12">
          {row?.service_start_date
            ? moment(row?.service_start_date).format("DD-MM-YYYY")
            : ""}
        </div>
      ),
    },
    {
      name: <div className="fs-12">Preauth Ref No</div>,
      sortable: false,
      cell: (row) => (
        <div className="fs-12">{row?.preauthorization_ref_no || ""}</div>
      ),
      width: "150px",
    },
    {
      name: <div className="fs-12">Invoice No.</div>,
      sortable: false,
      omit: false,
      selector: (row) => <div className="fs-12">{row?.invoice_no || ""}</div>,
    },
    {
      name: <div className="fs-12">Qty</div>,
      sortable: false,
      omit: false,
      selector: (row) => <div className="fs-12">{row?.quantity || 0}</div>,
    },
    {
      name: <div className="fs-12">Unit Price</div>,
      sortable: false,
      omit: false,
      selector: (row) => <div className="fs-12">{row?.unit_price || 0}</div>,
    },
    {
      name: <div className="fs-12">Disc.</div>,
      sortable: false,
      omit: false,
      selector: (row) => <div className="fs-12">{row?.discount || 0}</div>,
      width: "5rem",
    },
    {
      name: <div className="fs-12">Net Amt.</div>,
      sortable: false,
      omit: false,
      selector: (row) => <div className="fs-12">{row?.net_amount || 0}</div>,
    },
    {
      name: <Gear />,
      button: true,
      allowOverflow: true,
      cell: (row) =>
        isDisabled && Number(claimsSetting?.submit) === 1 ? (
          <div className="d-flex align-items-center fs-12 gap-2">
            <Eye
              onClick={() => viewModal(row?.id, row)}
              size={12}
              className="text-primary leaflet-interactive"
            />
            <Pen
              onClick={() => editModal(row?.id, row)}
              size={12}
              className="text-warning leaflet-interactive"
            />
            <MdDelete
              onClick={() => handleDeleteClick(row?.id)}
              size={12}
              className="text-danger leaflet-interactive"
            />
          </div>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <Eye
              onClick={() => viewModal(row?.id, row)}
              size={12}
              className="text-primary leaflet-interactive"
            />
          </div>
        ),
    },
  ];
  return (
    <div className="card border-0 shadow-sm ml-3">
      <div className="card-body p-0">
        {isDisabled && Number(claimsSetting?.submit) === 1 && (
          <Col xs={12} className="d-flex justify-content-end">
            <Button
              variant="success"
              size="sm"
              className="fs-12"
              onClick={() => {
                dispatch(setHistoryModal(true));
                dispatch(resetSelectedService());
              }}
            >
              <Plus /> Add Services
            </Button>
          </Col>
        )}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          <Nav variant="tabs">
            <Nav.Item className="ml-3">
              <Nav.Link
                eventKey="all-services"
                className={`d-flex gap-3 py-1 align-items-center rounded-0 justify-content-between ${
                  activeTab === "all-services"
                    ? "bg-secondary text-white border-0"
                    : ""
                } `}
              >
                <div className="d-flex align-items-center gap-2">
                  <FileEarmarkText size={12} />
                </div>
                <div className="d-flex flex-column gap-1 small mt-1">
                  <span className="fw-bold fs-12">All Invoices</span>
                  <span className="fs-12 text-muted">Total Overview</span>
                </div>
                <div className="fs-12 text-success fw-bold">
                  {cliamServicesList
                    ?.reduce(
                      (sum, service) => sum + Number(service.net_amount || 0),
                      0,
                    )
                    .toFixed(2)}
                </div>
              </Nav.Link>
            </Nav.Item>
            {invoiceKeys?.map((invoiceKey) => {
              const invoice = groupedInvoices[invoiceKey];
              const firstService = invoice.services[0];
              const invoiceDate = formatDate(firstService.service_start_date);

              return (
                <Nav.Item key={invoiceKey} className="ml-3">
                  <Nav.Link
                    eventKey={invoiceKey}
                    className={`d-flex gap-3 py-1 align-items-center rounded-0 justify-content-between ${
                      activeTab === invoiceKey
                        ? "bg-secondary text-white border-0"
                        : ""
                    } `}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <FileEarmarkText size={12} />
                    </div>
                    <div className="d-flex flex-column gap-1 small mt-1">
                      <span className="fw-bold fs-12">
                        Invoice{" "}
                        <span className="fs-12 text-info fw-normal">
                          {invoice.invoiceNumber}
                        </span>
                      </span>
                      <span className="fs-12 text-danger">{invoiceDate}</span>
                    </div>
                    <div className="fs-12 text-success fw-bold">
                      {invoice.totalAmount.toFixed(2)}
                    </div>
                  </Nav.Link>
                </Nav.Item>
              );
            })}
          </Nav>

          <Tab.Content className="p-3">
            <Tab.Pane eventKey="all-services">
              <Row>
                {cliamServicesList?.length > 0 ? (
                  <Col xs={12}>
                    <DataTable
                      data={cliamServicesList}
                      columns={columns}
                      responsive
                      customStyles={{
                        table: {
                          style: {
                            overflow: "visible",
                          },
                        },
                        rows: {
                          style: {
                            overflow: "visible",
                          },
                        },
                      }}
                    />
                  </Col>
                ) : (
                  <div className="d-flex mt-3 border fs-10 fw-bold text-danger py-3 justify-content-center">
                    No Services Added
                  </div>
                )}
              </Row>
            </Tab.Pane>
            {invoiceKeys.map((invoiceKey) => (
              <Tab.Pane key={invoiceKey} eventKey={invoiceKey}>
                <ServiceInformationDetails
                  services={groupedInvoices[invoiceKey].services}
                />
              </Tab.Pane>
            ))}
          </Tab.Content>
        </Tab.Container>

        <ConfirmationModal
          modal={showDeleteModal}
          setModal={setShowDeleteModal}
          onConfirm={handleConfirmDelete}
          title="Confirm Delete"
          description="Are you sure you want to delete this service?"
        />
      </div>
    </div>
  );
};

export default ServiceViewRequest;
